import {
  importProvidersFrom,
  NgModule,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import {
  BrowserModule,
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { PublicLayoutComponent } from './layouts/public-layout/public-layout.component';
import { HomeComponent } from './components/public/home/home.component';
import { HomeBannerComponent } from './components/public/home/home-banner/home-banner.component';
import { HomePricingComponent } from './components/public/home/home-pricing/home-pricing.component';
import { HomeCoverageGlobeComponent } from './components/public/home/home-coverage-globe/home-coverage-globe.component';
import { HomeProductDifferenceComponent } from './components/public/home/home-product-difference/home-product-difference.component';
import { HomeProductWalkthroughComponent } from './components/public/home/home-product-walkthrough/home-product-walkthrough.component';
import { HomeTestimonialsComponent } from './components/public/home/home-testimonials/home-testimonials.component';
import { HomeFinalCtaComponent } from './components/public/home/home-final-cta/home-final-cta.component';
import { HomeFooterComponent } from './components/public/home/home-footer/home-footer.component';
import { LoginComponent } from './components/public/login/login.component';
import { AuthCallbackComponent } from './components/public/auth-callback/auth-callback.component';
import { RegistrationLayoutComponent } from './layouts/registration-layout/registration-layout.component';
import { RegisterComponent } from './components/public/register/register.component';
import { ReviewSetupComponent } from './components/public/review-setup/review-setup.component';
import { VerifyEmailComponent } from './components/public/verify-email/verify-email.component';
import { VerificationCompleteComponent } from './components/public/verification-complete/verification-complete.component';
import { TermsCheckboxComponent } from './components/shared/terms-checkbox/terms-checkbox.component';

import { provideAuth } from 'angular-auth-oidc-client';
import { environment } from '../environments/environment';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { ModalModule } from 'ngx-bootstrap/modal';
import { NgxEchartsModule, NgxEchartsDirective } from 'ngx-echarts';
import { CommonModule } from '@angular/common';
import { BusyService } from './services/busy.service';
import { loadingInterceptor } from './interceptors/loading.interceptor';
import { jwtInterceptor } from './interceptors/jwt.interceptor';
import { unauthorizedInterceptor } from './interceptors/unauthorized.interceptor';
import { provideToastr } from 'ngx-toastr';
import { NgOtpInputModule } from 'ng-otp-input';
import { PhoneService } from './services/phone.service';
import { InViewDirective } from './directives/in-view.directive';
import { LoginRedirectGuard } from './guards/login-redirect.guard';

import { SharedModule } from './modules/shared.module';

@NgModule({
  declarations: [
    AppComponent,
    // Public / home page (always in initial bundle)
    PublicLayoutComponent,
    HomeComponent,
    HomeBannerComponent,
    HomePricingComponent,
    HomeCoverageGlobeComponent,
    HomeProductDifferenceComponent,
    HomeProductWalkthroughComponent,
    HomeTestimonialsComponent,
    HomeFinalCtaComponent,
    HomeFooterComponent,
    LoginComponent,
    AuthCallbackComponent,
    // Registration (linked from home CTA — keep eager)
    RegistrationLayoutComponent,
    RegisterComponent,
    ReviewSetupComponent,
    VerifyEmailComponent,
    VerificationCompleteComponent,
    TermsCheckboxComponent,
  ],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    CommonModule,
    InViewDirective,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    RecaptchaV3Module,
    ModalModule.forRoot(),
    NgOtpInputModule,
    SharedModule,
    NgxEchartsDirective,
  ],
  providers: [
    PhoneService,
    BusyService,
    provideAnimations(),
    provideToastr({
      positionClass: 'toast-bottom-right',
    }),
    provideHttpClient(
      withFetch(),
      withInterceptors([loadingInterceptor, jwtInterceptor, unauthorizedInterceptor])
    ),
    importProvidersFrom(
      ModalModule.forRoot(),
      RecaptchaV3Module,
      NgxEchartsModule.forRoot({
        echarts: () => import('echarts'),
      })
    ),
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptchaSiteKey,
    },
    provideAuth({
      config: {
        authority: environment.cognito.authority,
        redirectUrl: environment.cognito.redirectUrl,
        postLogoutRedirectUri: environment.cognito.postLogoutRedirectUri,
        clientId: environment.cognito.clientId,
        postLoginRoute: environment.cognito.postLoginRoute,
        scope: environment.cognito.scope,
        responseType: environment.cognito.responseType,
        silentRenew: environment.cognito.silentRenew,
        useRefreshToken: environment.cognito.useRefreshToken,
        disablePkce: environment.cognito.disablePkce,
      },
    }),
    provideClientHydration(withEventReplay()),
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {}
